/**
 * Gmail API v1 client for browser.
 */

const GMAIL_BASE = 'https://gmail.googleapis.com/gmail/v1/users/me';

export type GetAccessToken = (forceRefresh?: boolean) => Promise<string | null>;

export function createGmailClient(getAccessToken: GetAccessToken) {
  async function gmailFetch(path: string, retried = false, rateLimitRetries = 0): Promise<Response> {
    const token = await getAccessToken();
    if (!token) throw new Error('Not authenticated');

    const res = await fetch(`${GMAIL_BASE}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (res.status === 401 && !retried) {
      await getAccessToken(true);
      return gmailFetch(path, true, rateLimitRetries);
    }

    if (res.status === 429) {
      if (rateLimitRetries >= 3) throw new Error('Gmail API rate limit exceeded after 3 retries');
      const retryAfter = parseInt(res.headers.get('Retry-After') || '2', 10);
      await new Promise((r) => setTimeout(r, retryAfter * 1000));
      return gmailFetch(path, retried, rateLimitRetries + 1);
    }

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Gmail API ${res.status}: ${text}`);
    }

    return res;
  }

  async function getLabel(labelId: string): Promise<{ messagesTotal?: number; threadsTotal?: number }> {
    const res = await gmailFetch(`/labels/${labelId}`);
    const data = (await res.json()) as { messagesTotal?: number; threadsTotal?: number };
    return data;
  }

  async function searchThreads(
    query: string,
    onProgress?: (scanned: number, estimatedTotal?: number) => void,
    initialEstimate?: number
  ): Promise<{ id: string }[]> {
    const all: { id: string }[] = [];
    let pageToken = '';
    let estimatedTotal: number | undefined = initialEstimate;
    do {
      const params = new URLSearchParams({ q: query, maxResults: '500' });
      if (pageToken) params.set('pageToken', pageToken);
      const res = await gmailFetch(`/threads?${params}`);
      const data = (await res.json()) as {
        threads?: { id: string }[];
        nextPageToken?: string;
        resultSizeEstimate?: number;
      };
      if (data.threads) all.push(...data.threads);
      if (estimatedTotal === undefined && typeof data.resultSizeEstimate === 'number') {
        estimatedTotal = data.resultSizeEstimate;
      }
      onProgress?.(all.length, estimatedTotal);
      pageToken = data.nextPageToken || '';
    } while (pageToken);
    return all;
  }

  async function getThread(
    threadId: string,
    format: 'full' | 'metadata' | 'minimal' = 'full'
  ): Promise<GmailThread> {
    const res = await gmailFetch(`/threads/${threadId}?format=${format}`);
    return res.json();
  }

  async function getMessageRaw(messageId: string): Promise<{ raw?: string }> {
    const res = await gmailFetch(`/messages/${messageId}?format=raw`);
    return res.json();
  }

  async function getMessageFull(messageId: string): Promise<GmailMessage> {
    const res = await gmailFetch(`/messages/${messageId}?format=full`);
    return res.json();
  }

  async function getAttachment(
    messageId: string,
    attachmentId: string
  ): Promise<{ data?: string }> {
    const res = await gmailFetch(`/messages/${messageId}/attachments/${attachmentId}`);
    return res.json();
  }

  return {
    searchThreads,
    getLabel,
    getThread,
    getMessageRaw,
    getMessageFull,
    getAttachment,
  };
}

export interface GmailThread {
  id: string;
  messages?: GmailMessage[];
}

export interface GmailMessage {
  id?: string;
  threadId?: string;
  payload?: {
    filename?: string;
    body?: { attachmentId?: string; data?: string; size?: number };
    parts?: GmailMessage['payload'][];
    mimeType?: string;
  };
}

export function findAttachments(payload: GmailMessage['payload']): Array<{
  attachmentId?: string;
  inlineData?: string;
  filename: string;
  mimeType?: string;
}> {
  if (!payload) return [];
  const attachments: Array<{
    attachmentId?: string;
    inlineData?: string;
    filename: string;
    mimeType?: string;
  }> = [];
  const body = payload.body || {};
  if (payload.filename) {
    if (body.attachmentId) {
      attachments.push({
        attachmentId: body.attachmentId,
        filename: payload.filename,
        mimeType: payload.mimeType || 'application/octet-stream',
      });
    } else if (body.data && (body.size ?? 0) > 0) {
      attachments.push({
        inlineData: body.data,
        filename: payload.filename,
        mimeType: payload.mimeType || 'application/octet-stream',
      });
    }
  }
  for (const part of payload.parts || []) {
    attachments.push(...findAttachments(part));
  }
  return attachments;
}

export function decodeBase64url(str: string): Uint8Array {
  let b64 = str.replace(/-/g, '+').replace(/_/g, '/');
  while (b64.length % 4) b64 += '=';
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
